import styled from 'styled-components';

import { tint } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';
import { Theme } from '@savgroup-front-common/types';

import {
  FIELD_STATUS,
  InputStatus,
} from '../../atoms/Form/common/helpers/getFinalFieldState.types';
import { fontStyle } from '../../theme/helpers/fontStyle';

const imageSize = 35;

interface $SolutionProps {
  $status: InputStatus;
  $isDisabled?: boolean;
}

const getStatusColor = (
  {
    $status,
    theme,
  }: {
    $status: InputStatus;
    theme: Theme;
  },
  defaultColor?: string,
) => {
  switch ($status) {
    case FIELD_STATUS.ERROR:
      return theme.colors.danger;
    case FIELD_STATUS.WARNING:
      return theme.colors.alert;
    case FIELD_STATUS.SUCCESS:
      return theme.colors.success;
    default:
      return defaultColor || theme.colors.primary;
  }
};

export const $RadioCardInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  height: 100%;
`;

export const $RadioCardTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: ${fontWeight('bold')};
  color: ${({ theme }) => theme.colors.mainTextColor};
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};
`;

export const $PostTitle = styled.div`
  display: flex;
`;

export const $TextContainer = styled.div`
  flex: 1;
  margin-left: ${spacing('lg')};
  padding-right: ${spacing('xxl')};
  height: 100%;
  display: flex;
  align-items: center;
`;

export const $ImageContainer = styled.div<$SolutionProps>`
  width: ${rem(imageSize)};
  min-width: ${rem(imageSize)};
  height: ${rem(imageSize)};
  padding: ${spacing('sm')};
  border-width: ${spacing('px')};
  border: none;
  background: ${({ theme }) => theme.colors.white};
  background: none;
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};

  svg {
    width: 100%;
    height: auto;
  }
`;

export const $RadioCardLabel = styled.div`
  ${fontStyle('base')};
  font-weight: ${fontWeight('normal')};
  color: ${({ theme }) => theme.colors.paragraphTextColor};
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const $RadioCardBody = styled.div`
  ${fontStyle('base')};
  font-weight: ${fontWeight('normal')};
  color: ${({ theme }) => theme.colors.paragraphTextColor};
  padding-left: ${rem(imageSize + 14)};
`;

export const $RadioCardContainer = styled.div<$SolutionProps>`
  border-width: ${spacing('px')};
  border-style: solid;
  border-color: ${({ theme, $status }) =>
    getStatusColor({ theme, $status }, theme.colors.default)};
  background: ${({ $isDisabled, theme }) =>
    $isDisabled ? theme.colors.disabled : theme.colors.neutralBackground};
  border-radius: ${({ theme }) => theme.tw.radius.sm};

  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  padding: ${spacing('lg')};
  margin-bottom: ${spacing('lg')};
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};
`;

export const $RadioCardLabelContainer = styled.label<$SolutionProps>`
  position: relative;
  display: block;
  width: 100%;
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};

  input:hover,
  input:focus,
  input:active {
    ~ ${$RadioCardContainer} {
      border-color: ${({ $isDisabled }) =>
        $isDisabled ? null : getStatusColor};
    }
  }
  input:checked {
    ~ ${$RadioCardContainer} {
      border-color: ${getStatusColor};
      ${$RadioCardTitle} {
        color: ${getStatusColor};
      }
      ${$ImageContainer} {
        border-color: ${getStatusColor};
      }
      background-color: ${({ theme, $status }) =>
        tint(0.96, getStatusColor({ theme, $status }))};
    }
  }

  &:active {
    transform: ${({ $isDisabled }) => ($isDisabled ? null : 'scale(0.99)')};
  }

  input + * {
    transform: scale(1);
    top: unset;
    left: unset;
    position: relative;
    right: unset;
  }
  input:active + * {
    transform: scale(
      ${({ theme }) => theme.interactions.activeScale}
    ) !important;
  }
`;
